import React from "react";
import {
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Button,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  useNumberInput,
} from "@chakra-ui/react";
import { useController } from "react-hook-form";

import { maybePluralizeNoun } from "#app/utils/words.js";

export function StayLengthInput({ name, formContext, max = 14, ...rest }) {
  const {
    control,
    formState: { errors },
  } = formContext;

  const {
    field: { value, onChange, ref },
  } = useController({
    name,
    control,
    // Explicit defaultValue guards against RHF handing back `undefined`,
    // which would flip Chakra's useNumberInput into uncontrolled mode and
    // silently drop the field from the submitted form data.
    defaultValue: 1,
    rules: {
      required: { value: true, message: "Please input a number" },
      min: { value: 1, message: "Must stay at least one night" },
      max: { value: max, message: `Can't stay more than ${max} nights` },
    },
  });

  // Reset out of bounds value if new date range is selected
  // after the number of nights
  React.useEffect(() => {
    if (value > max) {
      onChange(max);
    }
  }, [value, max, onChange]);

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      // `name` is forwarded to the rendered <input> so the field survives
      // a native form submission if JS fails to intercept (in-app webview quirks).
      name,
      value,
      onChange: (v) => {
        onChange(v ? parseInt(v) : 0);
      },
      min: 1,
      max,
    });

  const nights = maybePluralizeNoun(value, "night", "s", false);

  return (
    <FormControl isInvalid={errors[name]} isRequired {...rest}>
      <FormLabel>How many nights would you like to stay?</FormLabel>
      <HStack maxW="320px">
        <Button {...getDecrementButtonProps()}>-</Button>
        <InputGroup maxW="125px">
          <Input {...getInputProps()} ref={ref} />
          <InputRightAddon
            children={nights}
            paddingX={2}
            bg="inherit"
            borderLeftWidth="0px"
          />
        </InputGroup>
        <Button {...getIncrementButtonProps()}>+</Button>
      </HStack>

      {errors[name] ? (
        <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
      ) : (
        // At the cap the "+" button silently stops working — say why. What
        // the picked days and nights will watch is shown below the input by
        // StaySummary.
        value >= max && (
          <FormHelperText>
            <Text color="warning" fontWeight="semibold">
              {max} nights is the longest stay we can watch.
            </Text>
          </FormHelperText>
        )
      )}
    </FormControl>
  );
}
